import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import {
  any,
  equals,
  includes,
  lensPath,
  lensProp,
  path,
  remove,
  set,
  view,
} from "ramda";
import { notBlank, notEmpty } from "../../../util/ext";
import { useCategories } from "../../vocabularyServices";
import { MediaContext } from "../../common/media-context";
import { matchSorter } from "match-sorter";

import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import AudienceSelect from "../../common/AudienceSelect";
import VirtualizedListbox from "../../../components/VirtualizedListbox";

const Categories = props => {
  const context = useContext(MediaContext);
  const categories = useCategories();
  const { className, showAudience, showFourCategories } = props;

  const [required, setRequired] = useState(true);
  useEffect(() => {
    const type = context.media.type;
    const required = includes("categories", context.requiredFields[type]);
    setRequired(required);
  }, [context.requiredFields, context.media.type]);

  const getOptionDisabled = option =>
    any(category => option.id === category.id, context.media.categories);

  const handleCategoryChange = useCallback(
    path => (event, value) => {
      if (notBlank(value)) {
        context.setMedia(prev => set(lensPath(path), value, prev));
      } else {
        const [list, index] = path;
        context.setMedia(prev =>
          set(
            lensProp(list),
            remove(index, 1, view(lensProp(list), prev)),
            prev,
          ),
        );
      }
    },
    [context],
  );

  const handleTextChange = useCallback(
    path => event => {
      const value = event.target.value;
      context.setMedia(prev => set(lensPath(path), value, prev));
    },
    [context],
  );

  const filterOptions = (options, { inputValue }) =>
    matchSorter(options, inputValue, {
      keys: ["headingDescription", "bisacCode"],
    });

  return (
    <div className={className}>
      <Grid item xs={12}>
        <Typography variant="h6">Categories</Typography>
      </Grid>
      {notEmpty(context.media) && (
        <Fragment>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable
                options={categories.data}
                value={path(["categories", 0], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                onChange={handleCategoryChange(["categories", 0])}
                filterOptions={filterOptions}
                renderOption={(props, cat) => (
                  <li {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      required={required}
                      type="text"
                      onBlur={() =>
                        context.setErrors(prev =>
                          context.validator.validateField(
                            context.media.type,
                            "categories",
                            context.media.categories[0],
                            prev,
                          ),
                        )
                      }
                      error={context.validator.hasError(
                        context.errors,
                        "categories",
                      )}
                      helperText={context.validator.firstError(
                        context.errors,
                        "categories",
                      )}
                      name={"media-category1-" + Math.random()}
                      variant="standard"
                      label="Category 1"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                options={categories.data}
                disabled={context.media.categories.length < 1}
                value={path(["categories", 1], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                onChange={handleCategoryChange(["categories", 1])}
                filterOptions={filterOptions}
                renderOption={(props, cat) => (
                  <li {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category2-" + Math.random()}
                      variant="standard"
                      label="Category 2"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {categories.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disabled={context.media.categories.length < 2}
                autoComplete
                options={categories.data}
                value={path(["categories", 2], context.media) || ""}
                getOptionLabel={cat =>
                  notBlank(cat) ? cat.headingDescription : ""
                }
                isOptionEqualToValue={(opt, val) =>
                  notBlank(val) && equals(opt.id, val.id)
                }
                getOptionDisabled={getOptionDisabled}
                ListboxComponent={VirtualizedListbox}
                ListboxProps={{ itemSize: 80 }}
                onChange={handleCategoryChange(["categories", 2])}
                filterOptions={filterOptions}
                renderOption={(props, cat) => (
                  <li {...props}>
                    <Tooltip
                      title={cat.headingDescription}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        primary={cat.headingDescription}
                        secondary={cat.bisacCode}
                      />
                    </Tooltip>
                  </li>
                )}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"media-category3-" + Math.random()}
                      variant="standard"
                      label="Category 3"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
          {showFourCategories && (
            <Grid item xs={12}>
              {categories.isLoading ? (
                <Skeleton variant="text" />
              ) : (
                <Autocomplete
                  disabled={context.media.categories.length < 3}
                  autoComplete
                  options={categories.data}
                  value={path(["categories", 3], context.media) || ""}
                  getOptionLabel={cat =>
                    notBlank(cat) ? cat.headingDescription : ""
                  }
                  isOptionEqualToValue={(opt, val) =>
                    notBlank(val) && equals(opt.id, val.id)
                  }
                  getOptionDisabled={getOptionDisabled}
                  ListboxComponent={VirtualizedListbox}
                  ListboxProps={{ itemSize: 80 }}
                  onChange={handleCategoryChange(["categories", 3])}
                  filterOptions={filterOptions}
                  renderOption={(props, cat) => (
                    <li {...props}>
                      <Tooltip
                        title={cat.headingDescription}
                        placement="bottom-start"
                      >
                        <ListItemText
                          primaryTypographyProps={{ noWrap: true }}
                          primary={cat.headingDescription}
                          secondary={cat.bisacCode}
                        />
                      </Tooltip>
                    </li>
                  )}
                  renderInput={params => {
                    params.inputProps["aria-autocomplete"] = "none";
                    params.inputProps["id"] = null;
                    return (
                      <TextField
                        {...params}
                        type="text"
                        name={"media-category4-" + Math.random()}
                        variant="standard"
                        label="Category 4"
                        fullWidth
                      />
                    );
                  }}
                />
              )}
            </Grid>
          )}
          {showAudience && (
            <Grid item xs={12}>
              <AudienceSelect
                value={path(["audienceCode"], context.media)}
                onChange={handleTextChange(["audienceCode"])}
              />
            </Grid>
          )}
        </Fragment>
      )}
    </div>
  );
};

export default Categories;
